import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { WarningMessage } from '@eika/attention-message';
import { ButtonTypes, LinkButton, StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { AppContext } from '../../containers/appContextContainer';
import {
    BarnKilde,
    BarnWeb,
    KjoennWeb,
    Omsorgsansvar,
    SFOWeb,
    SoeknadstypeWeb,
    StatusWeb,
    ValgtWeb,
} from '../../generated-types';
import useBarn, { useDeleteBarn, useUpdateBarn } from '../../hooks/useBarn';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import BarnValg from './barnValg';
import Barnebidrag from './barnebidrag';
import Foedselsdato from './foedselsdato';
import Foreldreansvar from './foreldreansvar';
import Kjoenn from './kjoenn';
import Sfo from './sfo';

const newBarn = {
    barnId: null,
    foedselsdato: undefined,
    kjoenn: KjoennWeb.IKKE_VALGT,
    omsorgsansvar: Omsorgsansvar.IKKE_VALGT,
    mottattBarnebidrag: undefined,
    betaltBarnebidrag: undefined,
    sfoPlass: SFOWeb.IKKE_VALGT,
    foreldre: [],
    kilde: BarnKilde.KUNDE,
    status: StatusWeb.CONFIRMED,
    visSfo: true,
    harMottattBarnebidrag: ValgtWeb.IKKE_VALGT,
    harBetaltBarnebidrag: ValgtWeb.IKKE_VALGT,
    harSfoPlass: ValgtWeb.IKKE_VALGT,
    harFellesForeldre: false,
};

export type BarnFormData = {
    harBarn: ValgtWeb;
    barn: {
        barnId: string | null;
        betaltBarnebidrag: number | undefined;
        foedselsdato: string | undefined;
        kilde: BarnKilde;
        kjoenn: KjoennWeb;
        mottattBarnebidrag: number | undefined;
        omsorgsansvar: Omsorgsansvar;
        sfoPlass: SFOWeb;
        visSfo: boolean;
        harMottattBarnebidrag: ValgtWeb;
        harBetaltBarnebidrag: ValgtWeb;
        harSfoPlass: ValgtWeb;
        harFellesForeldre: boolean;
    }[];
};

interface Props {
    navigateTo: string;
    navigateToOption?: string;
    navigateFrom: string;
}

const Barn: FC<Props> = ({ navigateTo, navigateFrom, navigateToOption = null }) => {
    const navigate = useNavigate();
    const { barn, button } = useTranslation();
    const { soeknadstype } = useContext(AppContext);
    const { data: barnContainer, isLoading, isFetched } = useBarn(soeknadstype);
    const nextNavigate =
        ((barnContainer?.soeknadstypeWeb === SoeknadstypeWeb.KJOEPE_BOLIG ||
            barnContainer?.soeknadstypeWeb === SoeknadstypeWeb.FINANSIERINGSBEVIS) &&
            navigateToOption) ||
        navigateTo;
    const methods = useForm<BarnFormData>({ defaultValues: { harBarn: ValgtWeb.IKKE_VALGT, barn: [] } });
    const {
        register,
        setValue,
        control,
        reset,
        handleSubmit,
        formState: { errors },
    } = methods;
    const { fields, append, remove } = useFieldArray({ name: 'barn', control });
    const { mutate, isPending: isUpdating } = useUpdateBarn(barnContainer || undefined, () => {
        navigate(nextNavigate);
    });
    const { mutate: deleteBarn } = useDeleteBarn(barnContainer?.soeknadId ? barnContainer.soeknadId : '');
    const harDsfBarn = barnContainer?.barn.some((b) => b.kilde === BarnKilde.DSF) || false;

    useEffect((): void => {
        if (barnContainer?.barn) {
            const barnList: BarnFormData = { harBarn: barnContainer.harBarn || ValgtWeb.IKKE_VALGT, barn: [] };
            barnContainer.barn.forEach((b) => {
                barnList.barn.push({
                    barnId: b.id,
                    betaltBarnebidrag: b.betaltBarnebidrag === 0 ? undefined : b.betaltBarnebidrag,
                    foedselsdato: b.foedselsdato,
                    kilde: b.kilde,
                    kjoenn: b.kjoenn,
                    mottattBarnebidrag: b.mottattBarnebidrag === 0 ? undefined : b.mottattBarnebidrag,
                    omsorgsansvar: b.omsorgsansvar,
                    sfoPlass: b.sfoPlass,
                    visSfo: b.visSfo,
                    harMottattBarnebidrag: b.harMottattBarnebidrag,
                    harBetaltBarnebidrag: b.harBetaltBarnebidrag,
                    harSfoPlass: b.harSfoPlass,
                    harFellesForeldre: b.foreldre.length > 1,
                });
            });
            reset(barnList);
        }
    }, [barnContainer, reset]);

    const onSubmitBarn = (data: BarnFormData) => {
        if (barnContainer) {
            const barnList: BarnWeb[] = [];
            data.barn.forEach((b1) => {
                const barn = barnContainer?.barn.find((b2) => b2.id === b1.barnId);
                if (barn) {
                    barn.omsorgsansvar = b1.omsorgsansvar;
                    barn.mottattBarnebidrag = b1.mottattBarnebidrag || 0;
                    barn.betaltBarnebidrag = b1.betaltBarnebidrag || 0;
                    barn.sfoPlass = b1.sfoPlass;
                    barnList.push(barn);
                } else {
                    barnList.push({
                        id: null,
                        foedselsdato: b1.foedselsdato || '',
                        kjoenn: b1.kjoenn,
                        omsorgsansvar: b1.omsorgsansvar,
                        mottattBarnebidrag: b1.mottattBarnebidrag || 0,
                        betaltBarnebidrag: b1.betaltBarnebidrag || 0,
                        sfoPlass: b1.sfoPlass,
                        foreldre: [barnContainer.laantakerFnr],
                        kilde: BarnKilde.KUNDE,
                        status: StatusWeb.CONFIRMED,
                        visSfo: b1.sfoPlass !== SFOWeb.INGEN,
                        harMottattBarnebidrag: b1.harMottattBarnebidrag,
                        harBetaltBarnebidrag: b1.harBetaltBarnebidrag,
                        harSfoPlass: b1.harSfoPlass,
                    });
                }
            });
            mutate({
                soeknadId: barnContainer?.soeknadId,
                soeknadStatus: barnContainer?.soeknadStatus,
                soeknadstypeWeb: barnContainer?.soeknadstypeWeb,
                laantakerFnr: barnContainer.laantakerFnr,
                harBarn: data.harBarn,
                barn: barnList,
                dsfBarnFeilet: barnContainer.dsfBarnFeilet,
            });
        } else {
            navigate(nextNavigate);
        }
    };

    const onClickLeggTilBarn = () => {
        append(newBarn);
    };

    const onClickSlett = (index: number) => {
        const { barnId } = fields[index];
        if (fields.length === 1) {
            setValue('harBarn', ValgtWeb.NEI, { shouldValidate: true });
        }
        if (barnId) {
            deleteBarn(barnId, {
                onSuccess: () => {
                    remove(index);
                },
            });
        } else {
            remove(index);
        }
    };

    const onClickHarBarn = (valg: ValgtWeb) => {
        if (valg === ValgtWeb.JA && fields.length === 0) {
            append(newBarn);
        } else if (valg === ValgtWeb.NEI) {
            fields.forEach((f, index) => {
                if (f.barnId) {
                    deleteBarn(f.barnId, {
                        onSuccess: () => {
                            remove(index);
                        },
                    });
                } else {
                    remove(index);
                }
            });
        }
    };

    return (
        <PageCard title={barn.title}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmitBarn)}>
                    <div className="row row-gap-large">
                        <span className="typography-ingress__light">{barn.subTitle}</span>
                        {isLoading && <Loader />}
                        {barnContainer?.dsfBarnFeilet && (
                            <WarningMessage id="barnWarning" text={barn.infoDsfError} icon={IconTypes.LUKK_24} />
                        )}
                        {isFetched && (fields.length === 0 || !harDsfBarn) && (
                            <BarnValg onClickHarBarn={onClickHarBarn} />
                        )}
                        <div>
                            {fields.map((field, index) => {
                                return (
                                    <div key={field.id} className="form-box-seagreen row-gap margin-bottom-small">
                                        {field.kilde === BarnKilde.DSF && (
                                            <span className="title">{`${
                                                field.kjoenn === KjoennWeb.GUTT ? barn.subTitleGutt : barn.subTitleJente
                                            } ${field.foedselsdato}`}</span>
                                        )}
                                        {field.kilde === BarnKilde.KUNDE && (
                                            <>
                                                <Foedselsdato index={index} control={control} errors={errors} />
                                                <Kjoenn index={index} control={control} errors={errors} />
                                            </>
                                        )}
                                        {!field.harFellesForeldre && (
                                            <>
                                                <Foreldreansvar
                                                    index={index}
                                                    register={register}
                                                    control={control}
                                                    setValue={setValue}
                                                    errors={errors}
                                                />
                                                <Barnebidrag
                                                    index={index}
                                                    control={control}
                                                    setValue={setValue}
                                                    errors={errors}
                                                />
                                            </>
                                        )}
                                        {field.visSfo && (
                                            <Sfo
                                                index={index}
                                                register={register}
                                                control={control}
                                                setValue={setValue}
                                                errors={errors}
                                            />
                                        )}
                                        {field.kilde === BarnKilde.KUNDE && (
                                            <div className="column">
                                                <LinkButton
                                                    icon={IconTypes.SLETT_24}
                                                    data-track-id="kreditt-boliglaan-barn-slett-ikon"
                                                    data-track-linkname="fjern"
                                                    onClick={() => onClickSlett(index)}
                                                >
                                                    {button.fjern}
                                                </LinkButton>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {fields.length > 0 && (
                                <div className="column">
                                    <StandardButton
                                        icon={IconTypes.PLUSS_36}
                                        buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                        onClick={onClickLeggTilBarn}
                                    >
                                        {button.leggTilBarn}
                                    </StandardButton>
                                </div>
                            )}
                        </div>
                    </div>
                    <PageFooter
                        soeknadId={barnContainer?.soeknadId}
                        onForrigeClick={() => navigate(navigateFrom)}
                        showSpinner={isUpdating}
                    />
                </form>
            </FormProvider>
        </PageCard>
    );
};

export default Barn;
